import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import PlatformConditional from 'Components/PlatformConditional';
import FormEditingHandler from 'Containers/FormEditingHandler';
import * as React from 'react';
import { filterByDocumentType } from 'Utility/documentHandling';
import { getClinicalStudyHeaderId } from '../utils';
import Form from './Form';
import BAEPHistory from './HistoryRowData/BAEP';
import BloodPressureHistory from './HistoryRowData/BloodPressure';
import ECGHistory from './HistoryRowData/ECG';
import EchocardiographyHistory from './HistoryRowData/Echocardiography';
import ENMGHistory from './HistoryRowData/ENMG';
import FVCHistory from './HistoryRowData/FVC';
import HeartRateHistory from './HistoryRowData/HeartRate';
import { useAppSelector } from 'Store/index';
import NocturnalPolygraphyHistory from './HistoryRowData/NocturnalPolygraphy';
import OCTSCANHistory from './HistoryRowData/OCTSCAN';
import SEPHistory from './HistoryRowData/SEP';
import VEPHistory from './HistoryRowData/VEP';

import type { TDocument } from '../utils';

const ClinicalStudyHistory = (documents: Array<TDocument>, startEdit: StartEdit): React.JSX.Element => {
  const FVCDocs = filterByDocumentType(documents, 'fvc');
  const ENMGDocs = filterByDocumentType(documents, 'enmg');
  const VEPDocs = filterByDocumentType(documents, 'vep');
  const SEPDocs = filterByDocumentType(documents, 'sep');
  const BAEPDocs = filterByDocumentType(documents, 'baep');
  const OCTSCANDocs = filterByDocumentType(documents, 'octscan');
  const ECGDocs = filterByDocumentType(documents, 'ecg');
  const EchocardiographyDocs = filterByDocumentType(documents, 'echocardiography');
  const BloodPressureDocs = filterByDocumentType(documents, 'bloodPressure');
  const HeartRateDocs = filterByDocumentType(documents, 'heartRate');
  const NocturnalPolygraphyDocs = filterByDocumentType(documents, 'nocturnalPolygraphy');

  return (
    <React.Fragment>
      <PlatformConditional platform={'ms'}>
        <React.Fragment>
          <VEPHistory documents={VEPDocs} startEdit={startEdit} />
          <SEPHistory documents={SEPDocs} startEdit={startEdit} />
          <BAEPHistory documents={BAEPDocs} startEdit={startEdit} />
          <OCTSCANHistory documents={OCTSCANDocs} startEdit={startEdit} />
          <ECGHistory documents={ECGDocs} startEdit={startEdit} />
          <EchocardiographyHistory documents={EchocardiographyDocs} startEdit={startEdit} />
          <BloodPressureHistory documents={BloodPressureDocs} startEdit={startEdit} />
          <HeartRateHistory documents={HeartRateDocs} startEdit={startEdit} />
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform={['sma', 'dmd']}>
        <React.Fragment>
          <FVCHistory documents={FVCDocs} startEdit={startEdit} />
          <ENMGHistory documents={ENMGDocs} startEdit={startEdit} />
          <NocturnalPolygraphyHistory documents={NocturnalPolygraphyDocs} startEdit={startEdit} />
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform={['polyneuro']}>
        <React.Fragment>
          <ENMGHistory documents={ENMGDocs} startEdit={startEdit} />
        </React.Fragment>
      </PlatformConditional>
    </React.Fragment>
  );
};

const ClinicalStudy = ({ documents }: IOwnProps): React.JSX.Element => {
  const platform = useAppSelector(s => s.session.platforms?.selected);
  return (
    <FormEditingHandler name="clinicalstudy" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name="clinicalstudy"
            headerId={getClinicalStudyHeaderId(documents, editing, platform)}
            editing={editing}
            editButtons={<div />}
          />
          {editing ? <Form formData={formData} document={documents.find(d => d._id === editing)} /> : null}
          {!editing && !view?.viewing ? ClinicalStudyHistory(documents, startEdit) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<TDocument>;
}

export default ClinicalStudy;
